import GenericHead from 'components/GenericHead';
import useTranslate from 'contexts/TranslateContext/useTranslate';
import { getPodcastUrl } from 'state/Podcast/helpers';
import { Helmet } from 'react-helmet';
import { HighlightsMetadataService } from 'services/HighlightsMetadataService';
import { highlightsPlayerLink } from 'utils/HighlightsUtils';
import { parse } from 'qs';
import { link as playerLink, meta as playerMeta } from 'heads/player';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  WIDGET_DIMENSIONS,
  WidgetDimensions,
} from 'constants/widgetDimensions';
import type { HighlightsMetadata } from 'api/highlights';

type Props = {
  description: string;
  imgUrl: string;
  location: Location;
  pathname: string;
  seedId: number;
  siteUrl: string;
  slug: string;
  title: string;
};

function PodcastEpisodesHead({
  description,
  imgUrl,
  location,
  seedId,
  slug,
  siteUrl,
  title,
}: Props) {
  const translate = useTranslate();
  const params = useParams<{ highlightsSlug?: string }>();
  const [highlightsMetadata, setHighlightsMetadata] =
    useState<HighlightsMetadata | null>(null);

  // Extract highlights slug from route params
  const { highlightsSlug } = params;

  // Fetch clip metadata when highlightsSlug is provided
  useEffect(() => {
    if (
      highlightsSlug &&
      HighlightsMetadataService.hasValidClipId(highlightsSlug)
    ) {
      HighlightsMetadataService.getHighlightsMetadata(highlightsSlug)
        .then(metadata => {
          if (metadata) {
            setHighlightsMetadata(metadata);
          }
        })
        .catch(() => {
          // Silently fail - use default podcast metadata
        });
    }
  }, [highlightsSlug]);

  const getDeeplink = useCallback(() => {
    const query = parse(location.search.slice(1));
    if (query.follow === 'true') {
      return `follow/podcast/${seedId}`;
    }
    return `play/custom/talk/show/${seedId}`;
  }, [location, seedId]);

  const pagePath = getPodcastUrl(seedId, slug);
  const { height, fixedWidth: width }: WidgetDimensions =
    WIDGET_DIMENSIONS.PODCAST_PROFILE ?? {};
  const podcastDescription = description?.replace(/\n+/g, ' ');

  // Use clip metadata if available, otherwise use podcast metadata
  const pageTitle = highlightsMetadata?.title || title;
  const pageDescription = highlightsMetadata?.description || podcastDescription;
  const pageImage =
    highlightsMetadata?.preview_image ||
    `${highlightsMetadata?.image}?ops=contain(1200,630)` ||
    imgUrl;

  return (
    <>
      <GenericHead
        deeplink={`goto/talk/show/${seedId}`}
        description={pageDescription}
        facebookImage={pageImage}
        image={pageImage}
        legacyDeeplink={getDeeplink()}
        ogType="video"
        title={pageTitle}
        twitterImage={pageImage}
      />
      <Helmet
        link={[
          ...playerLink({ pagePath, siteUrl, title: pageTitle }),
          // Preconnect to Highlights SDK domains for faster loading
          ...highlightsPlayerLink(),
        ]}
        meta={[
          {
            content: pageTitle,
            itemProp: 'name',
          },
          {
            content: translate(
              '{title}, Talk, Radio, Listen, On Demand, iHeartRadio, iHeart',
              {
                title: pageTitle,
              },
            ),
            name: 'keywords',
          },
          ...playerMeta({ pagePath, siteUrl, height, width }),
        ]}
      />
    </>
  );
}

export default PodcastEpisodesHead;
